import React, { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Storage as StorageIcon,
  TableChart as TableIcon,
  ViewColumn as ColumnIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import api from '../../utils/api';

const DEFAULT_PORTS = {
  postgresql: 5432,
  mysql: 3306,
  mssql: 1433,
};

const DatabaseConfig = ({ nodeId, moduleName, config = {}, onConfigChange }) => {
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [schemas, setSchemas] = useState([]);
  const [tables, setTables] = useState({});
  const [columns, setColumns] = useState({});
  const [expandedSchema, setExpandedSchema] = useState(null);
  const [expandedTable, setExpandedTable] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [browserLoading, setBrowserLoading] = useState(false);

  const isOutput = moduleName === 'database_write';

  const updateConfig = useCallback(
    (field, value) => {
      const newConfig = { ...config, [field]: value };
      // Auto-set port when db_type changes
      if (field === 'db_type' && DEFAULT_PORTS[value] !== undefined) {
        newConfig.port = DEFAULT_PORTS[value];
      }
      onConfigChange(nodeId, newConfig);
    },
    [nodeId, config, onConfigChange]
  );

  const getConnectionBody = () => ({
    connection_string: config.connection_string || undefined,
    db_type: config.db_type || 'postgresql',
    host: config.host || 'localhost',
    port: config.port || undefined,
    database: config.database || '',
    username: config.username || '',
    password: config.password || '',
  });

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post('/database/test-connection', getConnectionBody());
      setTestResult(data);
      if (data.success) {
        // Auto-load schemas on successful connection
        handleLoadSchemas();
      }
    } catch (err) {
      setTestResult({
        success: false,
        message: err.response?.data?.detail || err.message,
      });
    } finally {
      setTesting(false);
    }
  };

  const handleLoadSchemas = async () => {
    setBrowserLoading(true);
    try {
      const { data } = await api.post('/database/schemas', getConnectionBody());
      setSchemas(data.schemas || []);
    } catch {
      setSchemas([]);
    } finally {
      setBrowserLoading(false);
    }
  };

  const handleLoadTables = async (schemaName) => {
    if (expandedSchema === schemaName) {
      setExpandedSchema(null);
      return;
    }
    setExpandedSchema(schemaName);
    if (tables[schemaName]) return;

    try {
      const { data } = await api.post('/database/tables', {
        ...getConnectionBody(),
        schema_name: schemaName,
      });
      setTables((prev) => ({ ...prev, [schemaName]: data.tables || [] }));
    } catch {
      setTables((prev) => ({ ...prev, [schemaName]: [] }));
    }
  };

  const handleLoadColumns = async (schemaName, tableName) => {
    const key = `${schemaName}.${tableName}`;
    if (expandedTable === key) {
      setExpandedTable(null);
      return;
    }
    setExpandedTable(key);
    if (columns[key]) return;

    try {
      const { data } = await api.post('/database/columns', {
        ...getConnectionBody(),
        table: tableName,
        schema_name: schemaName,
      });
      setColumns((prev) => ({ ...prev, [key]: data.columns || [] }));
    } catch {
      setColumns((prev) => ({ ...prev, [key]: [] }));
    }
  };

  const handleTableClick = (schemaName, tableName) => {
    const qualified = schemaName && schemaName !== 'public'
      ? `${schemaName}.${tableName}`
      : tableName;
    if (!isOutput) {
      updateConfig('query', `SELECT * FROM ${qualified}`);
    } else {
      updateConfig('table', qualified);
    }
  };

  const handlePreview = async () => {
    if (!config.query) return;
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const { data } = await api.post('/database/preview', {
        ...getConnectionBody(),
        query: config.query,
        limit: 50,
      });
      setPreviewData(data);
    } catch (err) {
      setPreviewData({
        error: err.response?.data?.detail || err.message,
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <Box>
      {/* Test Connection */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleTestConnection}
          disabled={testing}
          startIcon={testing ? <CircularProgress size={16} /> : null}
        >
          Test Connection
        </Button>
        {testResult && (
          <Alert
            severity={testResult.success ? 'success' : 'error'}
            icon={testResult.success ? <CheckCircleIcon /> : <ErrorIcon />}
            sx={{ py: 0, flex: 1 }}
          >
            {testResult.message}
          </Alert>
        )}
      </Box>

      {/* Schema Browser */}
      {schemas.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Schema Browser
          </Typography>
          <Paper
            variant="outlined"
            sx={{ mb: 2, maxHeight: 300, overflow: 'auto' }}
          >
            {browserLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <List dense disablePadding>
                {schemas.map((schema) => (
                  <React.Fragment key={schema}>
                    <ListItemButton onClick={() => handleLoadTables(schema)}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <StorageIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={schema} />
                      {expandedSchema === schema ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </ListItemButton>
                    <Collapse
                      in={expandedSchema === schema}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List dense disablePadding sx={{ pl: 2 }}>
                        {(tables[schema] || []).map((tbl) => {
                          const tblKey = `${schema}.${tbl.name}`;
                          return (
                            <React.Fragment key={tbl.name}>
                              <ListItemButton
                                onClick={() =>
                                  handleLoadColumns(schema, tbl.name)
                                }
                                onDoubleClick={() =>
                                  handleTableClick(schema, tbl.name)
                                }
                              >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <TableIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={tbl.name}
                                  secondary={
                                    tbl.row_count != null
                                      ? `${tbl.row_count} rows`
                                      : undefined
                                  }
                                />
                                {expandedTable === tblKey ? (
                                  <ExpandLessIcon fontSize="small" />
                                ) : (
                                  <ExpandMoreIcon fontSize="small" />
                                )}
                              </ListItemButton>
                              <Collapse
                                in={expandedTable === tblKey}
                                timeout="auto"
                                unmountOnExit
                              >
                                <List dense disablePadding sx={{ pl: 2 }}>
                                  {(columns[tblKey] || []).map((col) => (
                                    <ListItem key={col.name} disablePadding>
                                      <ListItemButton sx={{ py: 0 }}>
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                          <ColumnIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText
                                          primary={col.name}
                                          secondary={`${col.type}${col.nullable ? ', nullable' : ''}`}
                                          primaryTypographyProps={{
                                            variant: 'body2',
                                          }}
                                          secondaryTypographyProps={{
                                            variant: 'caption',
                                          }}
                                        />
                                      </ListItemButton>
                                    </ListItem>
                                  ))}
                                </List>
                              </Collapse>
                            </React.Fragment>
                          );
                        })}
                      </List>
                    </Collapse>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </>
      )}

      <Divider sx={{ my: 1 }} />

      {/* SQL Editor / Table Config */}
      {isOutput ? (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Output Settings
          </Typography>
          <TextField
            label="Table Name"
            value={config.table || ''}
            onChange={(e) => updateConfig('table', e.target.value)}
            fullWidth
            size="small"
            margin="dense"
            helperText="Double-click a table in the browser to populate"
          />
          <FormControl fullWidth size="small" margin="dense">
            <InputLabel>Write Mode</InputLabel>
            <Select
              value={config.mode || 'append'}
              onChange={(e) => updateConfig('mode', e.target.value)}
              label="Write Mode"
            >
              <MenuItem value="append">Append</MenuItem>
              <MenuItem value="replace">Replace</MenuItem>
              <MenuItem value="upsert">Upsert</MenuItem>
            </Select>
          </FormControl>
          {config.mode === 'upsert' && (
            <TextField
              label="Upsert Key"
              value={config.upsert_key || ''}
              onChange={(e) => updateConfig('upsert_key', e.target.value)}
              fullWidth
              size="small"
              margin="dense"
              helperText="Column name for conflict resolution"
            />
          )}
        </>
      ) : (
        <>
          <Typography variant="subtitle2" gutterBottom>
            SQL Query
          </Typography>
          <TextField
            value={config.query || ''}
            onChange={(e) => updateConfig('query', e.target.value)}
            fullWidth
            multiline
            rows={4}
            size="small"
            margin="dense"
            placeholder="SELECT * FROM ..."
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
            helperText="Double-click a table in the browser to populate"
          />
          <Button
            variant="outlined"
            size="small"
            onClick={handlePreview}
            disabled={previewLoading || !config.query}
            startIcon={
              previewLoading ? (
                <CircularProgress size={16} />
              ) : (
                <PlayIcon />
              )
            }
            sx={{ mt: 1 }}
          >
            Preview
          </Button>
        </>
      )}

      {/* Preview Data Table */}
      {previewData && !previewData.error && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {previewData.row_count} rows returned
          </Typography>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: 300, mt: 0.5 }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {(previewData.columns || []).map((col) => (
                    <TableCell
                      key={col}
                      sx={{ fontWeight: 'bold', fontSize: 12, whiteSpace: 'nowrap' }}
                    >
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(previewData.rows || []).slice(0, 50).map((row, i) => (
                  <TableRow key={i} hover>
                    {(previewData.columns || []).map((col) => (
                      <TableCell key={col} sx={{ fontSize: 12 }}>
                        {row[col] != null ? String(row[col]) : ''}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      {previewData?.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {previewData.error}
        </Alert>
      )}
    </Box>
  );
};

export default DatabaseConfig;
